import { useEffect, useRef } from 'react';
import { VIDEO_SALES_LETTER_SECTION, PROSPECT_PORTAL_PAGE_SLUG } from '../../constants/liveWithUsPortalContent';
import { useSetPageProgress } from '../../contexts/ProspectPageProgressContext';
import { useTenantApiToken, useTenantProfile } from '../../contexts/TenantProfileContext';
import {
  fetchProspectClosingChecklist,
  fetchProspectGroup,
  fetchProspectLeaseSigning,
  getPortalContentPlayback,
  getProspectApplicationChecks,
  getProspectSelection,
  getProspectShortlist,
  getProspectTours,
  getProspectUnitAnalyses,
  getQualificationCall,
  type ProspectGroupPortalData,
  type ProspectMeData,
} from '../../services/tenantApi';
import { closingChecklistForSidebar } from '../closing/apply-closing-checklist';
import { applicationGroupProgress } from '../../utils/application-progress';
import { listBookedTours } from '../../utils/tour-home';
import {
  discoveryCallProgress,
  leaseSigningProgress,
  myInformationProgress,
  selectionProgress,
  shortlistProgress,
  tourBookingProgress,
  unitAnalysisProgress,
} from '../../utils/prospect-sidebar-progress';

/**
 * Fills the sidebar rings from saved prospect data so they show before
 * My Information or Discovery Call has been opened.
 */
export default function ProspectSidebarProgress() {
  const { kind, prospect } = useTenantProfile();
  const getToken = useTenantApiToken();
  const setPageProgress = useSetPageProgress();
  const getTokenRef = useRef(getToken);
  getTokenRef.current = getToken;

  useEffect(() => {
    if (kind !== 'prospect' || !prospect) return;
    const snapshot: ProspectMeData = prospect;
    let cancelled = false;

    void (async () => {
      const [groupResult, videoResult, callResult, applicationResult, shortlistResult, toursResult, analysisResult, selectionResult, closingResult, leaseResult] =
        await Promise.allSettled([
          fetchProspectGroup(getTokenRef.current),
          getPortalContentPlayback(getTokenRef.current, PROSPECT_PORTAL_PAGE_SLUG, VIDEO_SALES_LETTER_SECTION),
          getQualificationCall(getTokenRef.current),
          getProspectApplicationChecks(getTokenRef.current),
          getProspectShortlist(getTokenRef.current),
          getProspectTours(getTokenRef.current),
          getProspectUnitAnalyses(getTokenRef.current),
          getProspectSelection(getTokenRef.current),
          fetchProspectClosingChecklist(getTokenRef.current),
          fetchProspectLeaseSigning(getTokenRef.current),
        ]);
      if (cancelled) return;

      const group: ProspectGroupPortalData | null =
        groupResult.status === 'fulfilled' ? groupResult.value : snapshot.prospectGroup ?? null;
      setPageProgress('/my-information', myInformationProgress(snapshot, group), { ifAbsent: true });

      const videoDone = videoResult.status === 'fulfilled' && videoResult.value.engagement.watchedHalf === true;
      const call = callResult.status === 'fulfilled' ? callResult.value : null;
      const callShowed = call?.discoveryCallShowed === true;
      setPageProgress('/discovery-call', discoveryCallProgress(videoDone, callShowed), { ifAbsent: true });

      if (applicationResult.status === 'fulfilled' && applicationResult.value.applicants.length > 0) {
        const checks = applicationResult.value;
        setPageProgress('/apply-to-unit', applicationGroupProgress({
          applicants: checks.applicants,
          homes: checks.linkedUnitIds.length > 0 ? [{ linked: true }] : [],
        }), { ifAbsent: true });
      }

      if (shortlistResult.status === 'fulfilled') {
        setPageProgress('/shortlist-units', shortlistProgress(shortlistResult.value.shortlist.length), { ifAbsent: true });
      }
      if (toursResult.status === 'fulfilled') {
        const tours = toursResult.value;
        const bookingIds = [
          ...listBookedTours(tours.windows).map((tour) => tour.bookingId),
          ...tours.completedTours.map((tour) => tour.bookingId),
        ];
        setPageProgress('/book-tours', tourBookingProgress(bookingIds), { ifAbsent: true });
      }
      if (analysisResult.status === 'fulfilled') {
        setPageProgress(
          '/analyze-units',
          unitAnalysisProgress(
            analysisResult.value.units.map((unit) => ({
              availability: unit.availability,
              analyzed: unit.mine != null,
            })),
          ),
          { ifAbsent: true },
        );
      }
      const selection = selectionResult.status === 'fulfilled' ? selectionResult.value : null;
      if (selection) {
        setPageProgress('/selection', selectionProgress(selection.selectedUnitId), { ifAbsent: true });
      }
      const homeSelected = Boolean(
        selection?.selectedUnitId
        && selection.homes.some((home) => home.unitId === selection.selectedUnitId),
      );
      const closingChecklist = homeSelected && closingResult.status === 'fulfilled'
        ? closingChecklistForSidebar(
            closingResult.value,
            leaseResult.status === 'fulfilled' ? leaseResult.value : null,
            snapshot.personId,
          )
        : null;
      setPageProgress('/lease-signing', leaseSigningProgress(closingChecklist), { ifAbsent: true });
    })();

    return () => {
      cancelled = true;
    };
  }, [kind, prospect, setPageProgress]);

  return null;
}
